import React, {
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  useReducer,
  useCallback,
} from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";

import ColourContext from "./contexts/ColourContext";
import WheelSizeContext from "./contexts/WheelSizeContext";
import WindowContext from "../src/contexts/WindowContext";
import UserContext, {
  userReducer,
  initialUserState,
  UserState,
} from "./contexts/UserContext";
import LifeAreasContext, {
  initialLifeAreasState,
  lifeAreasReducer,
  LifeAreasState,
} from "./contexts/LifeAreasContext";
import SettingsContext, {
  settingsReducer,
  initialSettingsState,
  SettingsState,
} from "./contexts/SettingsContext";

import Background from "./components/background/Background";
import Personal from "./containers/Personal";
import Meet from "./containers/Meet";
import Buddies from "./containers/Buddies";
import TabNav from "./components/navigation/tabNav/TabNav";
import TopNav from "./components/navigation/topNav/TopNav";
import Onboarding from "./containers/Onboarding";
import Profile from "./containers/Profile";
import Menu from "./containers/Menu";

import { storageAvailable, setDataInLocalStorage } from "./utils/storage";

import style from "./App.module.css";
import ActivityContext, {
  activityReducer,
  initialActivityState,
} from "./contexts/ActivityContext";
import { act } from "react-dom/test-utils";

const returnWheelSizeFromWindowDimensions = (
  windowHeight: number,
  windowWidth: number
) => Math.min(480, Math.floor(Math.min(windowHeight, windowWidth) * 0.7));

// Main App
function App(props: object) {
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();
  // Window Dimensions (screen size)
  const [windowDimensions, setWindowDimensions] = useState({
    width: 750,
    height: 1334,
  });
  // Wheel Dimensions (width/height of the personal wheel)
  const [wheelSize, setWheelSize] = useState(() => {
    return returnWheelSizeFromWindowDimensions(
      window.innerHeight,
      window.innerWidth
    );
  });
  // Colour state
  const [colourThemeKey, setColourThemeKey] = useState("cyan");
  // const colour = colourThemes[colourThemeKey];

  // User
  const [user, userDispatch] = useReducer(userReducer, initialUserState);
  // Life Areas
  const [lifeAreas, dispatchLifeAreas] = useReducer(
    lifeAreasReducer,
    initialLifeAreasState
  );
  // User Activity
  const [activity, dispatchActivity] = useReducer(
    activityReducer,
    initialActivityState
  );
  // App Settings
  const [settings, settingsDispatch] = useReducer(
    settingsReducer,
    initialSettingsState
  );
  // Modals
  const [showProfile, setShowProfile] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  // Installation
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Updates the window dimensions when resizing has finished
  const handleResize = useCallback(() => {
    const windowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    setTimeout(() => {
      if (
        windowSize.width === window.innerWidth &&
        windowSize.height === window.innerHeight
      ) {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        // Max wheel size = 540px. 80% of the window width/height.
        const wheelSize = returnWheelSizeFromWindowDimensions(
          windowHeight,
          windowWidth
        );
        // Set to State
        setWindowDimensions({
          width: windowWidth,
          height: windowHeight,
        });
        setWheelSize(wheelSize);
      }
    }, 300);
  }, []);

  // Update the window and wheel dimensions on window resize.
  useLayoutEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  // If there is no stored data redirect to the welcome/onboarding
  useEffect(() => {
    // Check the browser support for local storage
    if (storageAvailable("localStorage")) {
      // Check if data has been saved already
      if (localStorage.getItem("user")) {
        // Load User
        const user = localStorage.getItem("user");
        let userState: UserState;
        if (user) {
          userState = JSON.parse(user);
          console.log("loaded user:", userState);
          userDispatch({ type: "SET_DATA", data: userState });
        }
        // Load Life Areas
        const lifeAreas = localStorage.getItem("lifeAreas");
        let lifeAreasState: LifeAreasState;
        if (lifeAreas) {
          lifeAreasState = JSON.parse(lifeAreas);
          console.log("loaded life areas:", lifeAreasState);
          dispatchLifeAreas({ type: "SET_DATA", data: lifeAreasState });
        }
        // Load User Activity - TODO: this should be loaded when needed
        const activity = localStorage.getItem("activity");
        if (activity) {
          console.log("loaded activity:", activity);
          const activityState = JSON.parse(activity);
          dispatchActivity({ type: "SET_ALL_DATA", data: activityState });
        }
        // Load Colour
        const colourKey = localStorage.getItem("colourKey");
        if (colourKey) setColourThemeKey(JSON.parse(colourKey));
        // Load App Settings
        const settings = localStorage.getItem("settings");
        let settingsState: SettingsState;
        if (settings) {
          settingsState = JSON.parse(settings);
          console.log("loaded settings:", settingsState);
          settingsDispatch({ type: "SET_DATA", data: settingsState });
        }
        setIsLoading(false);
        if (location.pathname === "/") history.push("/personal");
      } else {
        // Nothing saved, redirect
        setIsLoading(false);
        console.log("No user data, redirecting to onboarding");
        history.push("/welcome");
      }
    } else {
      alert("Sorry, this browser is not supported. Try Chrome or Safari.");
    }
  }, []);

  const installPWAPrompt = useRef<any>();

  // https://web.dev/promote-install/
  // https://love2dev.com/pwa/add-to-homescreen/
  // https://developers.google.com/web/fundamentals

  // Handling adding to homescreen (PWA Install)
  // This will only run once (once it's been installed it won't show again)
  useEffect(() => {
    // Function for showing prompt
    const handleShowInstallPrompt = (timestamp: number) => {
      setShowInstallPrompt(true);
      settingsDispatch({
        type: "SET_LAST_INSTALL_PROMPT",
        timestamp: timestamp,
      });
      setDataInLocalStorage("settings", {
        ...settings,
        lastInstallPrompt: timestamp,
      });
    };
    window.addEventListener("beforeinstallprompt", (e) => {
      console.log("beforeinstallprompt event listener fired");
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      installPWAPrompt.current = e;
      // Update UI to notify the user they can add to home screen
      // Check if they have already seen a prompt recently. Only show 24 hours apart.
      const timeNow = Math.floor(Date.now() / 1000); // timestamp in seconds
      if (settings.lastInstallPrompt > 0) {
        console.log(
          "timeNow",
          timeNow,
          "lastInstallPrompt",
          settings.lastInstallPrompt
        );
        if (timeNow - settings.lastInstallPrompt > 86400) {
          console.log("Showing install prompt - not seen for over 24hrs");
          handleShowInstallPrompt(timeNow);
        } else {
          console.log("Not showing install prompt - seen too recently");
        }
      } else {
        // Show for first time
        console.log("show prompt for first time");
        handleShowInstallPrompt(timeNow);
      }
    });
    window.addEventListener("appinstalled", (e) => {
      // Log install to analytics
      console.log("appinstalled event fired");
      settingsDispatch({ type: "SET_INSTALLED_PWA" });
      setDataInLocalStorage("settings", { ...settings, installedPWA: true });
    });
  }, [settings]);

  const installButtonClicked = () => {
    console.log("installButtonClicked", installPWAPrompt.current);
    // Show the prompt
    if (!installPWAPrompt.current) return console.log("No prompt available");
    installPWAPrompt.current.prompt();
    // Wait for the user to respond to the prompt
    installPWAPrompt.current.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the A2HS prompt");
        setShowInstallPrompt(false);
      } else {
        console.log("User dismissed the A2HS prompt");
        setShowInstallPrompt(false);
      }
      installPWAPrompt.current = null;
    });
  };

  return isLoading ? (
    <main>Loading Data</main>
  ) : (
    <SettingsContext.Provider
      value={{ settings: settings, dispatchSettings: settingsDispatch }}
    >
      <ColourContext.Provider
        value={{ key: colourThemeKey, set: setColourThemeKey }}
      >
        <WindowContext.Provider value={windowDimensions}>
          <WheelSizeContext.Provider value={wheelSize}>
            {/* TODO: Rename "state" to "user" ? - as done in settings */}
            <UserContext.Provider
              value={{ state: user, dispatch: userDispatch }}
            >
              <LifeAreasContext.Provider
                value={{ lifeAreas, dispatchLifeAreas }}
              >
                <ActivityContext.Provider
                  value={{ activity, dispatchActivity }}
                >
                  <main>
                    <Background />
                    <Switch>
                      {/* Onboarding */}
                      <Route path="/welcome" exact={true}>
                        <Onboarding />
                      </Route>
                      {/* Main App */}
                      <Route path="/:sectionKey">
                        <TopNav
                          menuPressed={() => setShowMenu(true)}
                          profilePressed={() => setShowProfile(true)}
                        />
                        <section className={style.sections}>
                          <Switch>
                            <Route path="/personal">
                              <WheelSizeContext.Provider value={wheelSize}>
                                <Personal />
                              </WheelSizeContext.Provider>
                            </Route>
                            <Route path="/buddies" exact={true}>
                              <Buddies />
                            </Route>
                            <Route path="/meet" exact={true}>
                              <Meet />
                            </Route>
                          </Switch>
                        </section>
                        <TabNav />
                        <Profile
                          show={showProfile}
                          closePressed={() => setShowProfile(false)}
                        />
                        <Menu
                          show={showMenu}
                          closePressed={() => setShowMenu(false)}
                        />
                        {showInstallPrompt && (
                          <div className={style.installBanner}>
                            <span>
                              Add to your homescreen for a better experience.
                              It's fast and free!
                            </span>
                            <div>
                              <button onClick={installButtonClicked}>
                                INSTALL
                              </button>
                              <button
                                onClick={() => setShowInstallPrompt(false)}
                              >
                                NOT NOW
                              </button>
                            </div>
                          </div>
                        )}
                      </Route>
                    </Switch>
                    {/* Dashboard? */}
                  </main>
                </ActivityContext.Provider>
              </LifeAreasContext.Provider>
            </UserContext.Provider>
          </WheelSizeContext.Provider>
        </WindowContext.Provider>
      </ColourContext.Provider>
    </SettingsContext.Provider>
  );
}

export default App;
